import React, { useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { useMyProfile } from '../../../hooks/useProfile';
import { toast } from 'react-toastify';
import { X, Save, AlertCircle, User, AtSign, Mail, FileText } from 'lucide-react';
import { CommunityError } from "../../../errors/sentryErrors";
import { trackEvent } from '../../../utils/analytics';

const EditProfileDialog = ({ isOpen, onClose, onProfileUpdated }) => {
  const { data: profileRes, isLoading, mutate } = useMyProfile();
  const profile = profileRes?.profile;
  
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [handle, setHandle] = useState('');
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (profile && isOpen) {
      setName(profile.name || '');
      setBio(profile.bio || '');
      setHandle(profile.handle || '');
      setErrors({});
    }
  }, [profile, isOpen]);

  useEffect(() => {
    trackEvent('profile_edit_opened', {});
  }, []);

  if (!isOpen) return null;

  let canChangeHandle = true;
  let daysLeft = 0;
  if (profile?.handleChangedAt) {
    const changeDate = new Date(profile.handleChangedAt);
    const now = new Date();
    const diffTime = now.getTime() - changeDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 30) {
      canChangeHandle = false;
      daysLeft = 30 - diffDays;
    }
  }

  const handleHandleChange = (e) => {
    let val = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setHandle(val);
    if (errors.handle) setErrors({ ...errors, handle: null });
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if nothing changed
    if (
      name === (profile.name || '') &&
      bio === (profile.bio || '') &&
      handle === (profile.handle || '')
    ) {
      handleClose();
      return;
    }

    // Local validation
    const newErrors = {};
    if (!name || name.length > 80) newErrors.name = "Name must be 1-80 characters.";
    if (bio && bio.length > 500) newErrors.bio = "Bio cannot exceed 500 characters.";
    if (handle !== profile.handle) {
      if (!/^[a-z0-9_]{3,30}$/.test(handle)) {
        newErrors.handle = "Handle must be 3-30 chars: letters, numbers, underscore only.";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const payload = {};
      if (name !== profile.name) payload.name = name;
      if (bio !== profile.bio) payload.bio = bio || null; // API accepts null for empty
      if (handle !== profile.handle) payload.handle = handle;

      await mutate(payload);
      trackEvent('profile_updated', {
        name_changed: payload.name !== undefined,
        bio_changed: payload.bio !== undefined,
        handle_changed: payload.handle !== undefined,
      });
      toast.success("Profile updated");
      if (onProfileUpdated) onProfileUpdated();
      handleClose();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      if (err.status === 400 && err.message?.includes("HANDLE")) {
         setErrors({ handle: "Handle must be 3-30 chars: letters, numbers, underscore only." });
      } else if (err.status === 409) {
         setErrors({ handle: "This handle is taken." });
      } else if (err.status === 429 && err.daysLeft) {
         toast.error(`You can change your handle again in ${err.daysLeft} days.`);
         setErrors({ handle: `You can change your handle again in ${err.daysLeft} days.` });
      } else if (err.status === 400) {
         setErrors({ general: err.message || "Validation failed" });
      } else {
         toast.error("An unexpected error occurred.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl flex justify-center">
           <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900">Edit Profile</h2>
          <button onClick={handleClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-50 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 flex-1">
          {errors.general && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm flex items-start gap-2">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{errors.general}</span>
            </div>
          )}

          <div className="space-y-4">
            {/* Display Name */}
            <div className="text-left">
              <label className="block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                <User size={16} className="text-gray-400" /> Display Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: null });
                }}
                maxLength={80}
                required
                className={`w-full px-4 py-2.5 rounded-xl border ${errors.name ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:border-teal-500 focus:ring-teal-100'} focus:ring-4 outline-none transition-all`}
                placeholder="Your name"
              />
              {errors.name && <p className="text-red-500 text-xs mt-1 font-medium">{errors.name}</p>}
            </div>

            {/* Handle */}
            <div className="text-left">
              <label className="block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                <AtSign size={16} className="text-gray-400" /> Handle
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-gray-400 font-medium">@</span>
                <input
                  type="text"
                  value={handle}
                  onChange={handleHandleChange}
                  disabled={!canChangeHandle}
                  className={`w-full pl-9 pr-4 py-2.5 rounded-xl border ${errors.handle ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:border-teal-500 focus:ring-teal-100'} ${!canChangeHandle ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''} focus:ring-4 outline-none transition-all`}
                  placeholder="username"
                />
              </div>
              {errors.handle && <p className="text-red-500 text-xs mt-1 font-medium">{errors.handle}</p>}
              {!canChangeHandle && !errors.handle && (
                <p className="text-amber-600 text-xs mt-1 font-medium flex items-center gap-1">
                  <AlertCircle size={12} />
                  You can change your handle again in {daysLeft} days.
                </p>
              )}
            </div>

            {/* Bio */}
            <div className="text-left">
              <div className="flex justify-between items-end mb-1.5">
                <label className="block text-sm font-bold text-gray-700 flex items-center gap-2">
                  <FileText size={16} className="text-gray-400" /> Bio
                </label>
                <span className={`text-xs font-medium ${bio.length > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                  {bio.length} / 500
                </span>
              </div>
              <textarea
                value={bio}
                onChange={(e) => {
                  setBio(e.target.value);
                  if (errors.bio) setErrors({ ...errors, bio: null });
                }}
                rows={3}
                className={`w-full px-4 py-2.5 rounded-xl border ${errors.bio ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:border-teal-500 focus:ring-teal-100'} focus:ring-4 outline-none transition-all resize-none`}
                placeholder="Tell us about your travel style..."
              />
              {errors.bio && <p className="text-red-500 text-xs mt-1 font-medium">{errors.bio}</p>}
            </div>

            {/* Email (Readonly) */}
            <div className="text-left">
              <label className="block text-sm font-bold text-gray-700 mb-1.5 flex items-center gap-2">
                <Mail size={16} className="text-gray-400" /> Email
              </label>
              <input
                type="email"
                value={profile?.email || ''}
                readOnly
                disabled
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed outline-none"
              />
              <p className="text-gray-400 text-xs mt-1">Email cannot be changed.</p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="px-5 py-2.5 rounded-xl font-bold text-sm text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-2.5 rounded-xl font-bold text-sm transition-colors shadow-lg shadow-teal-200 disabled:opacity-50"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <Save size={16} />
              )}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProfileDialog;
